import React, { useState } from 'react';
import { useSlotManagement } from '../hooks/useSlotManagement';
import { SaveSlotPolicyRequest, SlotPolicyDto } from '../types/slotPolicy.types';
import SlotPolicyModal from './SlotPolicyModal';
import ConfirmModal from '@/shared/components/feedback/ConfirmModal';
import Pagination from '@/shared/components/dataDisplay/Pagination';
import '../styles/SlotManagementPage.css';

const PAGE_SIZE = 15;

const SlotManagementPage: React.FC = () => {
    const { policies, loading, hasGlobalPolicy, isGlobalPolicy, savePolicy, deletePolicy } = useSlotManagement();

    const [nameFilter, setNameFilter] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [modalOpen, setModalOpen] = useState(false);
    const [editTarget, setEditTarget] = useState<SlotPolicyDto | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<SlotPolicyDto | null>(null);

    const handleOpenAdd = () => {
        setEditTarget(null);
        setModalOpen(true);
    };

    const handleOpenEdit = (policy: SlotPolicyDto) => {
        setEditTarget(policy);
        setModalOpen(true);
    };

    const handleSave = async (req: SaveSlotPolicyRequest) => {
        const success = await savePolicy(req);
        if (success) {
            setModalOpen(false);
            setCurrentPage(1);
        }
    };

    const handleDeleteConfirm = async () => {
        if (!deleteTarget) return;
        const success = await deletePolicy(deleteTarget.seqNo);
        if (success) setDeleteTarget(null);
    };

    const formatDate = (d: string | null | undefined) => {
        if (!d || d.length !== 8) return '즉시 적용';
        return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
    };

    const filteredPolicies = (nameFilter.trim()
            ? policies.filter(p => p.policyName.includes(nameFilter.trim()))
            : [...policies]
    ).sort((a, b) => {
        const ag = isGlobalPolicy(a) ? 0 : 1;
        const bg = isGlobalPolicy(b) ? 0 : 1;
        return ag - bg;
    });

    const totalPages = Math.ceil(filteredPolicies.length / PAGE_SIZE);
    const pagedPolicies = filteredPolicies.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

    return (
        <div className="slot-page-content">
            <div className="slot-page-header">
                <h1 className="slot-page-title">슬롯 관리</h1>
                <div className="slot-header-right">
                    <input
                        type="text"
                        className="slot-search-input"
                        value={nameFilter}
                        onChange={(e) => {
                            setNameFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                        placeholder="그룹명 검색"
                    />
                    <button className="slot-add-btn" onClick={handleOpenAdd}>
                        + 정책 추가
                    </button>
                </div>
            </div>

            <div className="slot-result-count">
                <i className="fas fa-list" />
                총 <strong>{filteredPolicies.length}</strong>개의 슬롯 정책
            </div>

            <div className="slot-table-wrap">
                {loading ? (
                    <div className="slot-empty">불러오는 중...</div>
                ) : filteredPolicies.length === 0 ? (
                    <div className="slot-empty">
                        {nameFilter.trim() ? '검색 결과가 없습니다.' : '등록된 슬롯 정책이 없습니다.'}
                    </div>
                ) : (
                    <table className="slot-table">
                        <thead>
                        <tr>
                            <th>그룹명</th>
                            <th>표준권역</th>
                            <th>적용 시작일</th>
                            <th>필수 슬롯 수</th>
                            <th>수락률</th>
                            <th>슬롯 수</th>
                            <th>사용 여부</th>
                            <th>관리</th>
                        </tr>
                        </thead>
                        <tbody>
                        {pagedPolicies.map((policy) => {
                            const global = isGlobalPolicy(policy);
                            return (
                                <tr key={policy.seqNo} className={global ? 'row-global' : undefined}>
                                    <td className="text-center">
                                        {policy.policyName}
                                        {global && <span className="badge-global">기본</span>}
                                    </td>
                                    <td className="text-center">
                                        {global
                                            ? <span className="badge-global-area">모든 협력사 적용</span>
                                            : (policy.standardAreas?.join(', ') || '-')}
                                    </td>
                                    <td>{formatDate(policy.applyStartDate)}</td>
                                    <td>{policy.requiredSlotCnt ?? '-'}</td>
                                    <td>{policy.acceptanceRate != null ? `${policy.acceptanceRate}%` : '-'}</td>
                                    <td>{policy.slots?.length ?? 0}개</td>
                                    <td>
                                            <span className={policy.useYn === 'Y' ? 'badge-use-y' : 'badge-use-n'}>
                                                {policy.useYn}
                                            </span>
                                    </td>
                                    <td>
                                        <button className="btn-edit" onClick={() => handleOpenEdit(policy)}>
                                            수정
                                        </button>
                                        <button className="btn-delete" onClick={() => setDeleteTarget(policy)}>
                                            삭제
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                )}
            </div>

            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

            <SlotPolicyModal
                isOpen={modalOpen}
                editTarget={editTarget}
                hasGlobalPolicy={hasGlobalPolicy}
                onSave={handleSave}
                onClose={() => setModalOpen(false)}
            />

            <ConfirmModal
                isOpen={!!deleteTarget}
                title="슬롯 정책 삭제"
                message={`'${deleteTarget?.policyName}' 정책을 삭제하시겠습니까?`}
                confirmText="삭제"
                cancelText="취소"
                onConfirm={handleDeleteConfirm}
                onCancel={() => setDeleteTarget(null)}
            />
        </div>
    );
};

export default SlotManagementPage;
